import {
  getConversations,
  getConversation,
  getMessages,
  createMessage,
  updateMessage,
  setStatus,
  transferConversation,
  setConversationTag,
} from '@/api/conversations'
import { resolveAccountId } from '@/store/helpers/account'

const state = {
  records: [],
  messagesByConversation: {},
  selectedId: null,
  // A thread opened outside the list (search, a link) lives here: the poll
  // rewrites records wholesale and would drop it
  selectedRecord: null,
  uiFlags: {
    isFetching: false,
    isFetchingMessages: false,
    isSending: false,
    // Polling refetches the list every couple of seconds; the loading
    // placeholder must only show before the very first response
    hasFetched: false,
  },
}

export const getters = {
  getConversations: $state => $state.records,
  getSelectedId: $state => $state.selectedId,
  // The list record is the fresh one, the opened dialog carries extras the list
  // never sends — ratings and the right to rate
  getSelectedConversation: $state => {
    const listed = $state.records.find(c => c.id === $state.selectedId)
    const opened =
      $state.selectedRecord?.id === $state.selectedId
        ? $state.selectedRecord
        : null
    if (!listed && !opened) return null

    return { ...opened, ...listed }
  },
  getMessages: $state => id => $state.messagesByConversation[id] || [],
  getUIFlags: $state => $state.uiFlags,
  getUnreadTotal: $state =>
    $state.records.reduce(
      (total, record) => total + (record.unread_count || 0),
      0
    ),
  // Per conversation, so an alert can tell a new message from a thread that
  // was merely marked read elsewhere
  getUnreadByConversation: $state =>
    $state.records.reduce((counts, record) => {
      if (record.unread_count) counts[record.id] = record.unread_count
      return counts
    }, {}),
}

export const actions = {
  async get({ commit, dispatch, rootGetters }) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    if (!accountId) return
    commit('SET_UI_FLAG', { isFetching: true })
    try {
      const { data } = await getConversations(accountId)
      commit('SET_CONVERSATIONS', data)
    } finally {
      commit('SET_UI_FLAG', { isFetching: false })
    }
  },

  async fetchMessages({ commit, dispatch, rootGetters }, id) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    commit('SET_UI_FLAG', { isFetchingMessages: true })
    try {
      const { data } = await getMessages(accountId, id)
      commit('SET_MESSAGES', { id, messages: data })
    } finally {
      commit('SET_UI_FLAG', { isFetchingMessages: false })
    }
  },

  async select({ commit, dispatch, rootGetters }, id) {
    commit('SET_SELECTED', id)
    if (!id) return

    // Always fetched: the list holds only recent closed threads, and it never
    // sends the ratings or the right to rate
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    const { data } = await getConversation(accountId, id)
    commit('SET_SELECTED_RECORD', data)
  },

  async editMessage(
    { commit, dispatch, rootGetters },
    { id, messageId, content }
  ) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    commit('SET_UI_FLAG', { isSending: true })
    try {
      const { data } = await updateMessage(accountId, id, messageId, {
        content,
      })
      commit('REPLACE_MESSAGE', { id, message: data })
    } finally {
      commit('SET_UI_FLAG', { isSending: false })
    }
  },
  async sendMessage(
    { commit, dispatch, rootGetters },
    { id, content, replyToId, note }
  ) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    commit('SET_UI_FLAG', { isSending: true })
    try {
      // Attachments travel with the draft, so the payload is plain json
      const payload = { content, reply_to_id: replyToId, note }
      const { data } = await createMessage(accountId, id, payload)
      commit('APPEND_MESSAGE', { id, message: data })
    } finally {
      commit('SET_UI_FLAG', { isSending: false })
    }
  },

  async setStatus({ commit, dispatch, rootGetters }, { id, status }) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    const { data } = await setStatus(accountId, id, status)
    commit('UPDATE_CONVERSATION', data)
  },

  async transfer({ commit, dispatch, rootGetters }, { id, ...payload }) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    const { data } = await transferConversation(accountId, id, payload)
    commit('UPDATE_CONVERSATION', data)
  },

  async setTag({ commit, dispatch, rootGetters }, { id, tagId }) {
    const accountId = await resolveAccountId({ dispatch, rootGetters })
    const { data } = await setConversationTag(accountId, id, tagId)
    commit('UPDATE_CONVERSATION', data)
  },
}

export const mutations = {
  // Keeps the previous array when nothing visible changed, so the list does
  // not redraw and the empty placeholder does not blink on every poll
  SET_CONVERSATIONS($state, records) {
    $state.uiFlags = { ...$state.uiFlags, hasFetched: true }
    const same =
      $state.records.length === records.length &&
      records.every((record, index) => {
        const prev = $state.records[index]
        return (
          prev.id === record.id &&
          prev.last_activity_at === record.last_activity_at &&
          prev.unread_count === record.unread_count &&
          prev.state === record.state &&
          (prev.assignee?.id ?? null) === (record.assignee?.id ?? null) &&
          (prev.tag?.id ?? null) === (record.tag?.id ?? null) &&
          prev.contact.name === record.contact.name &&
          prev.contact.blocked === record.contact.blocked
        )
      })
    if (same) return

    $state.records = records
  },
  UPDATE_CONVERSATION($state, conversation) {
    const index = $state.records.findIndex(c => c.id === conversation.id)
    if (index === -1) {
      $state.records = [conversation, ...$state.records]
    } else {
      $state.records.splice(index, 1, conversation)
    }
  },
  SET_SELECTED($state, id) {
    $state.selectedId = id
    if ($state.selectedRecord?.id !== id) $state.selectedRecord = null
  },
  SET_SELECTED_RECORD($state, conversation) {
    $state.selectedRecord = conversation
  },
  // Polling refetches the whole thread; keep the previous array when nothing
  // changed so the thread does not redraw and lose the scroll position
  SET_MESSAGES($state, { id, messages }) {
    const existing = $state.messagesByConversation[id]
    if (existing && existing.length === messages.length) {
      const same = messages.every(
        (message, index) =>
          existing[index].id === message.id &&
          existing[index].status === message.status &&
          existing[index].content === message.content &&
          existing[index].edited_at === message.edited_at &&
          // Without this the edit button stays after the channel window closes
          existing[index].editable === message.editable
      )
      if (same) return
    }

    $state.messagesByConversation = {
      ...$state.messagesByConversation,
      [id]: messages,
    }
  },
  REPLACE_MESSAGE($state, { id, message }) {
    const existing = $state.messagesByConversation[id] || []
    $state.messagesByConversation = {
      ...$state.messagesByConversation,
      [id]: existing.map(m => (m.id === message.id ? message : m)),
    }
  },
  APPEND_MESSAGE($state, { id, message }) {
    const existing = $state.messagesByConversation[id] || []
    if (existing.some(m => m.id === message.id)) return
    $state.messagesByConversation = {
      ...$state.messagesByConversation,
      [id]: [...existing, message],
    }
  },
  SET_UI_FLAG($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
